/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';



/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';






import OpmsField from '../../components/OpmsForm/OpmsField';

import OpmsInput from '../../components/OpmsForm/OpmsInput';

import TextField from '../../components/OpmsForm/Fields/TextField';

import Select from '../../components/OpmsForm/Fields/Select';

import ToggleButtonGroup from '../../components/OpmsForm/Fields/ToggleButtonGroup';

import { Chip, InputAdornment, Paper } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import PatientBreadcrumbs from '../../components/Breadcrumbs/PatientBreadcrumbs';
import DatePicker from '../../components/OpmsForm/Fields/DatePicker';
import NumericField, { FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE } from '../../components/OpmsForm/Fields/NumericField';
import OpmsStaticGrid, { IOpmsStaticGridHeader } from '../../components/OpmsForm/Grids/Static/OpmsStaticGrid';
import OpmsStaticRow, { IOpmsStaticRow } from '../../components/OpmsForm/Grids/Static/OpmsStaticRow';
import OpmsFieldGroup from '../../components/OpmsForm/OpmsFieldGroup';
import OpmsFieldLabel from '../../components/OpmsForm/OpmsFieldLabel';
import TrialForm from '../../components/OpmsForm/TrialForm';
import Checkbox from '../../components/OpmsForm/Fields/Checkbox';
import OpmsConditional, { IOpmsContionParams } from '../../components/OpmsForm/OpmsConditional';
import { PatientContext } from '@ngt/online-patient-management-ui';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IHaematologyAndBiochemistryProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(0)
    },
    subHeading: {
        padding: theme.spacing(3)
    },
    instructions: {
        padding: theme.spacing(3),
        fontWeight: 'bold'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const t3Required = ({ lookups, formState }: IOpmsContionParams<Dtos.HaematologyAndBiochemistry>) => formState.values.t3NotDone !== true;

const biochemistryHeader: IOpmsStaticGridHeader[] = [
    {
        columnName: 'test',
        content: 'Test',
        width: '20%',
        minWidth: 300
    },
    {
        columnName: 'type',
        content: '',
        width: '20%',
        minWidth: 200
    },
    {
        columnName: 'value',
        content: 'Value',
        width: '20%',
        minWidth: 200
    },
    {
        columnName: 'lln',
        content: 'LLN',
        width: '20%',
        minWidth: 200
    },
    {
        columnName: 'uln',
        content: 'ULN',
        width: '20%',
        minWidth: 200
    }
];

const ThyroidFunctionHeader: IOpmsStaticGridHeader[] = [
    {
        columnName: 'test',
        content: 'Test',
        width: '30%',
        minWidth: 300
    },
    {
        columnName: 'notDone',
        content: 'Not Done',
        width: '10%',
        
    },
    {
        columnName: 'value',
        content: 'Value',
        width: '20%',
        minWidth: 200
    },
    {
        columnName: 'lln',
        content: 'LLN',
        width: '20%',
        minWidth: 200
    },
    {
        columnName: 'uln',
        content: 'ULN',
        width: '20%',
        minWidth: 200
    }
];

const bilirubinRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Bilirubin'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            µmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'Bilirubin'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            µmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'BilirubinUln'
    }
];

const aspartateTransaminaseRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'AspartateTransaminase'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            IU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'AspartateTransaminase'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            IU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'AspartateTransaminaseUln'
    }
];

const alanineTransaminaseRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'AlanineTransaminase'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            IU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'AlanineTransaminase'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            IU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'AlanineTransaminaseUln'
    }
];

const alkalinePhosphataseRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'AlkalinePhosphatase'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            IU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'AlkalinePhosphatase'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            IU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'AlkalinePhosphataseUln'
    }
];

const serumCreatinineRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'SerumCreatinine'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            µmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'SerumCreatinine'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            µmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'SerumCreatinineLln'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            µmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'SerumCreatinineUln'
    }
];

const orContainerStyles: React.CSSProperties = {
    position: 'relative'
};

const orStyles: React.CSSProperties = {
    position: 'absolute',
    top: '-36px',
    margin: '0 auto',
    width: '42px',
    left: '0',
    right: '0'
}

const calculatedCreatinineClearanceRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'CalculatedCreatinineClearance'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mL/min&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'CalculatedCreatinineClearance'
    }
];

const calciumRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Calcium'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'Calcium'
    }
];

const lactateDehydrogenaseRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'LactateDehydrogenase'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'LactateDehydrogenase'
    }
];

const totalProteinRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'TotalProtein'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            g/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'TotalProtein'
    }
];

const amylaseRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Amylase'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'Amylase'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'AmylaseLln'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'AmylaseUln'
    }
];

const lipaseRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Lipase'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'Lipase'
    },

    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'LipaseLln'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'LipaseUln'
    }
];

const bloodUreaNitrogenRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'BloodUreaNitrogen'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mg/dL&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'BloodUreaNitrogen'
    }
];

const serumUreaRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <div style={orContainerStyles}>
                <div style={orStyles}>
                    <Chip label="OR" />
                </div>
                <OpmsFieldLabel />
            </div>
        ),
        fieldName: 'SerumUrea'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mg/dL&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'SerumUrea'
    }
];

const potassiumRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Potassium'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'Potassium'
    }
];

const gammaGlutamylTransferaseRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'GammaGlutamylTransferase'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            IU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'GammaGlutamylTransferase'
    }
];

const sodiumRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Sodium'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'Sodium'
    }
];

const uricAcidRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'UricAcid'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'UricAcid'
    }
];

const cortisolRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Cortisol'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            nmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'Cortisol'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            nmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'CortisolLln'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            nmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'CortisolUln'
    }
];

const bloodGlucoseRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'BloodGlucose'
    },
    {
        columnName: 'type',
        content: (
            <OpmsInput
                component={Select}
            />
        ),
        fieldName: 'BloodGlucoseTypeId'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'BloodGlucose'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'BloodGlucoseLln'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'BloodGlucoseUln'
    }
];

//const haemoglobinHeader: IOpmsStaticGridHeader[] = [
//    {
//        columnName: 'test',
//        content: '',
//        width: '71%'
//    },
//    {
//        columnName: 'value',
//        content: 'Value',
//        width: '20%'
//    },
//    {
//        columnName: 'unit',
//        content: 'Unit',
//        width: '6.5%'
//    },
//];

//const haemoglobinRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
//    {
//        columnName: 'test',
//        content: (
//            <OpmsFieldLabel />
//        ),
//        fieldName: 'Haemoglobin'
//    },
//    {
//        columnName: 'value',
//        content: (
//            <OpmsInput
//                component={TextField}
//            />
//        ),
//        fieldName: 'Haemoglobin'
//    },
//    {
//        columnName: 'unit',
//        content: (
//            <OpmsInput
//                component={Select}
//                initialValue={Dtos.LookupGramPerUnitType.GL}
//            />
//        ),
//        fieldName: 'HaemoglobinUnitId'
//    },
//];

const tshRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'ThyroidStimulatingHormone'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mIU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'ThyroidStimulatingHormone'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mIU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'TshLln'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mIU/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'TshUln'
    }
];

const t3Row: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Triiodothyronine'
    },
    {
        columnName: 'notDone',
        content: (
            <OpmsInput
                component={Checkbox}
                label=""
            />
        ),
        fieldName: 'T3NotDone'
    },
    {
        columnName: 'value',
        content: (
            <OpmsConditional
                formClass={Dtos.HaematologyAndBiochemistry}
                condition={t3Required}
                mode="Enable"
                subscription={{ values: true }}
            >
                <OpmsInput
                    component={NumericField}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                pmol/L&nbsp;
                            </InputAdornment>
                        )
                    }}
                    numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
                />
            </OpmsConditional>
            
        ),
        fieldName: 'Triiodothyronine'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsConditional
                formClass={Dtos.HaematologyAndBiochemistry}
                condition={t3Required}
                mode="Enable"
                subscription={{ values: true }}
            >
                <OpmsInput
                    component={NumericField}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                pmol/L&nbsp;
                            </InputAdornment>
                        )
                    }}
                    numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
                />
            </OpmsConditional>
        ),
        fieldName: 'T3Lln'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsConditional
                formClass={Dtos.HaematologyAndBiochemistry}
                condition={t3Required}
                mode="Enable"
                subscription={{ values: true }}
            >
                <OpmsInput
                    component={NumericField}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                pmol/L&nbsp;
                            </InputAdornment>
                        )
                    }}
                    numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
                />
            </OpmsConditional>
        ),
        fieldName: 'T3Uln'
    }
];

const t4Row: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Thyroxine'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            pmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'Thyroxine'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            pmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'T4Lln'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            pmol/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'T4Uln'
    }
];

const troponinRow: Array<IOpmsStaticRow<Dtos.HaematologyAndBiochemistry>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'Troponin'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            ng/mL&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'Troponin'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            ng/mL&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'TroponinLln'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            ng/mL&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'TroponinUln'
    }
];

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const HaematologyAndBiochemistry: React.FunctionComponent<IHaematologyAndBiochemistryProps> = () => {
    const classes = useStyles();

    const [patient] = React.useContext(PatientContext);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;

    return (
        <PageLayout
            breadcrumbs={
                <PatientBreadcrumbs />
            }
            heading="Haematology and Biochemistry"
        >
            <Paper
                className={classes.container}
            >
                <TrialForm
                    formClass={Dtos.HaematologyAndBiochemistry}
                >
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Haematology
                        </Typography>
                        <OpmsField name="HaematologyDate">
                            <OpmsInput
                                component={DatePicker}
                            />
                        </OpmsField>
                        <OpmsFieldGroup>
                            <OpmsField
                                name="Haemoglobin"
                            >
                                <OpmsInput
                                    component={TextField}
                                />
                            </OpmsField>
                            <OpmsField
                                name="HaemoglobinUnitId"
                            >
                                <OpmsInput
                                    component={Select}
                                    initialValue={Dtos.LookupGramPerUnitType.GL}
                                />
                            </OpmsField>
                        </OpmsFieldGroup>
                        {
                            //<OpmsStaticGrid
                            //    headers={haemoglobinHeader}
                            //>
                            //    <OpmsStaticRow
                            //        cells={haemoglobinRow}
                            //    />
                            //</OpmsStaticGrid>
                        }
                        <OpmsField name="RedBloodCellCount">
                            <OpmsInput
                                component={TextField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography color="textSecondary">x10<sup>12</sup>/L or pL&nbsp;</Typography>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </OpmsField>
                        <OpmsField name="TotalNeutrophils">
                            <OpmsInput
                                component={TextField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography color="textSecondary">x10<sup>9</sup>/L&nbsp;</Typography>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </OpmsField>
                        <OpmsField name="Lymphocytes">
                            <OpmsInput
                                component={TextField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography color="textSecondary">x10<sup>9</sup>/L&nbsp;</Typography>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </OpmsField>
                        <OpmsField name="Platelets">
                            <OpmsInput
                                component={TextField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography color="textSecondary">x10<sup>9</sup>/L&nbsp;</Typography>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </OpmsField>
                        <OpmsField name="WhiteBloodCellCount">
                            <OpmsInput
                                component={TextField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography color="textSecondary">x10<sup>9</sup>/L&nbsp;</Typography>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </OpmsField>
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Biochemistry
                        </Typography>
                        <OpmsField name="BiochemistryDate">
                            <OpmsInput
                                component={DatePicker}
                            />
                        </OpmsField>
                        <OpmsField name="GilbertSyndromeId">
                            <OpmsInput
                                component={ToggleButtonGroup}
                            />
                        </OpmsField>
                        <OpmsStaticGrid
                            headers={biochemistryHeader}
                        >
                            <OpmsStaticRow
                                cells={bilirubinRow}
                            />
                            <OpmsStaticRow
                                cells={aspartateTransaminaseRow}
                            />
                            <OpmsStaticRow
                                cells={alanineTransaminaseRow}
                            />
                            <OpmsStaticRow
                                cells={alkalinePhosphataseRow}
                            />
                            {
                                !patientCohortC && 
                                <OpmsStaticRow
                                    cells={serumCreatinineRow}
                                />
                            }
                            <OpmsStaticRow
                                cells={calculatedCreatinineClearanceRow}
                            />
                            <OpmsStaticRow
                                cells={calciumRow}
                            />
                            <OpmsStaticRow
                                cells={lactateDehydrogenaseRow}
                            />
                            <OpmsStaticRow
                                cells={totalProteinRow}
                            />
                            <OpmsStaticRow
                                cells={patientCohortC ? amylaseRow : amylaseRow.filter(x => x.columnName === 'test' || x.columnName === 'value')}
                            />
                            <OpmsStaticRow
                                cells={lipaseRow}
                            />
                            <OpmsStaticRow
                                cells={bloodUreaNitrogenRow}
                            />
                            <OpmsStaticRow
                                cells={serumUreaRow}
                            />
                            <OpmsStaticRow
                                cells={potassiumRow}
                            />
                            <OpmsStaticRow
                                cells={gammaGlutamylTransferaseRow}
                            />
                            <OpmsStaticRow
                                cells={sodiumRow}
                            />
                            <OpmsStaticRow
                                cells={uricAcidRow}
                            />
                            {
                                patientCohortC && <OpmsStaticRow
                                    cells={cortisolRow}
                                />
                            }
                            {
                                patientCohortC && <OpmsStaticRow
                                    cells={bloodGlucoseRow}
                                />
                            }
                        </OpmsStaticGrid>
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Thyroid Function
                        </Typography>

                        {
                            patientCohortC && <Typography
                                className={classes.instructions}
                            >
                                TSH + T4 are mandatory, T3 is optional (provide if available)
                            </Typography>
                        }

                        <OpmsField name="ThyroidFunctionDate">
                            <OpmsInput
                                component={DatePicker}
                            />
                        </OpmsField>
                        {
                            patientCohortC && <OpmsStaticGrid
                                headers={ThyroidFunctionHeader}
                            >
                                <OpmsStaticRow
                                    cells={tshRow}
                                />
                                <OpmsStaticRow
                                    cells={t3Row}
                                />
                                <OpmsStaticRow
                                    cells={t4Row}
                                />
                            </OpmsStaticGrid>
                        }
                        {
                            !patientCohortC && <OpmsField
                                name="ThyroidStimulatingHormone"
                            >
                                <OpmsInput
                                    component={NumericField}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                mIU/L&nbsp;
                                            </InputAdornment>
                                        )
                                    }}
                                    numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
                                />
                            </OpmsField>
                        }
                        { 
                            !patientCohortC && <OpmsField
                                name="Triiodothyronine"
                            >
                                <OpmsInput
                                    component={NumericField}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                pmol/L&nbsp;
                                            </InputAdornment>
                                        )
                                    }}
                                    numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
                                />
                            </OpmsField>
                        }
                        {
                            !patientCohortC && <OpmsField
                                name="Thyroxine"
                            >
                                <OpmsInput
                                    component={NumericField}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                pmol/L&nbsp;
                                            </InputAdornment>
                                        )
                                    }}
                                    numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
                                />
                            </OpmsField>
                        }
                    </OpmsFieldGroup>
                    {
                        patientCohortC && <OpmsFieldGroup>
                            <Typography
                                variant="h2"
                                color="primary"
                                className={classes.subHeading}
                            >
                                Troponin
                            </Typography>
                            <OpmsField name="TroponinDate">
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsStaticGrid
                                headers={biochemistryHeader}
                            >
                                <OpmsStaticRow
                                    cells={troponinRow}
                                />
                            </OpmsStaticGrid>
                        </OpmsFieldGroup>
                    }
                </TrialForm>
            </Paper>
        </PageLayout>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default HaematologyAndBiochemistry;